import BaseController from './base_controller'

export default class extends BaseController {
  static targets = ['form', 'panel', 'groupPanel', 'groupInput', 'moveLink', 'moveHeader', 'count', 'tabsContainer', 'panelsContainer', 'tab', 'sortableTab', 'activeGroupArea', 'groupLabel', 'removeLink', 'addVesselWrapper', 'groupModal', 'groupForm', 'confirmModal']
  static values = { type: String }

  get activeGroupId() {
    return this.tabTargets.find(t => t.classList.contains('is-active'))?.dataset.groupId || ''
  }

  groupNameForId(groupId) {
    if (!groupId) return this.tabTargets.find(t => t.dataset.panel === 'default')?.dataset.groupName
    return this.sortableTabTargets.find(t => t.dataset.groupId === groupId)?.dataset.groupName
  }

  connect() {
    this.boundGroupAddedHandler = (event) => this.addMoveLink(event)
    window.addEventListener('vessel-group:groupAdded', this.boundGroupAddedHandler)
    this.boundGroupCreatedHandler = (event) => this.handleGroupCreated(event)
    window.addEventListener('vessel-group:created', this.boundGroupCreatedHandler)
    this.boundGroupConfirmHandler = (event) => this.handleGroupConfirm(event)
    window.addEventListener('vessel-group:confirm', this.boundGroupConfirmHandler)
    this.boundVesselCreatedHandler = (event) => this.handleVesselCreated(event)
    window.addEventListener('vessel:created', this.boundVesselCreatedHandler)
    this.boundGroupDestroyedHandler = (event) => this.handleGroupDestroyed(event)
    window.addEventListener('vessel-group:destroyed', this.boundGroupDestroyedHandler)
    this.boundTabSelected = (event) => this.handleTabSelected(event.detail.panel)
    this.element.addEventListener('component-tab-group:tab-selected', this.boundTabSelected)
    this.boundUpdateCounts = () => this.updateCounts()
    window.addEventListener('vessel-group:changed', this.boundUpdateCounts)

    requestAnimationFrame(() => this.autoSelectGroup())
  }

  disconnect() {
    window.removeEventListener('vessel-group:groupAdded', this.boundGroupAddedHandler)
    window.removeEventListener('vessel-group:created', this.boundGroupCreatedHandler)
    window.removeEventListener('vessel-group:confirm', this.boundGroupConfirmHandler)
    window.removeEventListener('vessel:created', this.boundVesselCreatedHandler)
    window.removeEventListener('vessel-group:destroyed', this.boundGroupDestroyedHandler)
    this.element.removeEventListener('component-tab-group:tab-selected', this.boundTabSelected)
    window.removeEventListener('vessel-group:changed', this.boundUpdateCounts)
  }

  handleTabSelected(panelId) {
    this.updateActiveGroupLabel(panelId)
    localStorage.setItem(`active_vessel_group_${this.typeValue}`, panelId || 'default')
  }

  handleVesselCreated(event) {
    const { imo } = event.detail

    const activeGroupPanel = this.groupPanelTargets.find(p => p.classList.contains('is-active'))
    const panelId = activeGroupPanel?.dataset.panel || 'default'
    const currentGroupId = panelId !== 'default' ? panelId : ''
    if (!currentGroupId) return

    requestAnimationFrame(() => {
      const newForm = this.formTargets.find(f => f.dataset.imo === imo)
      if (!newForm) return

      const groupInput = this.groupInputTargets.find(i => newForm.contains(i))
      if (groupInput) {
        groupInput.value = currentGroupId
      }

      this.moveLinkTargets.filter(l => newForm.contains(l)).forEach(l => l.classList.remove('d-none'))
      this.moveLinkTargets.find(l => newForm.contains(l) && l.dataset.groupId === currentGroupId)
        ?.classList.add('d-none')
    })
  }

  handleGroupConfirm(event) {
    if (this.hasConfirmModalTarget) this.confirmModalTarget.remove()
    this.element.insertAdjacentHTML('beforeend', event.detail.confirmHtml)
  }

  confirmModalTargetConnected(el) {
    el.showModal()
  }

  handleGroupCreated(event) {
    const { tabHtml, panelHtml, moveLinkTemplate, groupName } = event.detail

    this.groupModalTarget.close()
    this.groupFormTarget.reset()

    this.tabsContainerTarget.insertAdjacentHTML('beforeend', tabHtml)
    this.panelsContainerTarget.insertAdjacentHTML('beforeend', panelHtml)

    this.addMoveLink({ detail: { template: moveLinkTemplate } })
    this.updateCounts()

    window.dispatchEvent(new CustomEvent('notify', { detail: { message: `Group '${groupName}' created` }, bubbles: true }))
  }

  handleGroupDestroyed(event) {
    const { groupId } = event.detail

    const tab = this.tabTargets.find(t => t.dataset.panel === groupId)
    const wasActive = tab?.classList.contains('is-active')
    this.sortableTabTargets.find(t => t.dataset.groupId === groupId)?.remove()

    const defaultPanel = this.panelTargets.find(p => p.dataset.panel === 'default')
    const defaultAddVesselWrapper = this.addVesselWrapperTargets.find(w => w.dataset.panel === 'default')

    this.formTargets
      .filter(form => this.groupInputTargets.find(i => form.contains(i))?.value === groupId)
      .forEach(form => {
        const input = this.groupInputTargets.find(i => form.contains(i))
        if (input) input.value = ''

        const moveToDefaultLink = this.moveLinkTargets.find(l => form.contains(l) && l.dataset.groupId === '')
        if (moveToDefaultLink) moveToDefaultLink.classList.add('d-none')

        if (defaultAddVesselWrapper) defaultPanel.insertBefore(form, defaultAddVesselWrapper)
        else if (defaultPanel) defaultPanel.appendChild(form)
      })

    this.moveLinkTargets.filter(l => l.dataset.groupId === groupId).forEach(l => l.remove())
    if (this.moveLinkTargets.filter(l => l.dataset.groupId !== '').length === 0) {
      this.moveHeaderTargets.forEach(h => h.classList.add('d-none'))
    }

    this.groupPanelTargets.find(p => p.dataset.panel === groupId)?.remove()
    this.updateCounts()
    if (this.hasConfirmModalTarget) this.confirmModalTarget.close()

    if (wasActive) {
      window.dispatchEvent(new CustomEvent('select-tab', { detail: { panel: 'default' }, bubbles: true }))
    }
  }

  autoSelectGroup() {
    const savedId = localStorage.getItem(`active_vessel_group_${this.typeValue}`)
    if (savedId === 'default') return
    if (savedId) {
      const savedPanel = this.panelTargets.find(p => p.dataset.panel === savedId)
      if (savedPanel && this.formTargets.some(f => savedPanel.contains(f))) {
        window.dispatchEvent(new CustomEvent('select-tab', { detail: { panel: savedId } }))
        return
      }
    }

    const firstNonEmptyGroup = this.panelTargets.find(p =>
      p.dataset.panel !== 'default' && this.formTargets.some(f => p.contains(f))
    )
    if (firstNonEmptyGroup) {
      window.dispatchEvent(new CustomEvent('select-tab', { detail: { panel: firstNonEmptyGroup.dataset.panel } }))
    }
  }

  updateActiveGroupLabel(panelId) {
    const isDefault = !panelId || panelId === 'default'
    if (this.hasActiveGroupAreaTarget) {
      this.activeGroupAreaTarget.classList.toggle('vessel-group-label--hidden', isDefault)
    }

    if (isDefault) return
    const sortableTab = this.sortableTabTargets.find(tab => tab.dataset.groupId === panelId)
    if (!sortableTab) return

    if (this.hasGroupLabelTarget) {
      this.groupLabelTarget.textContent = sortableTab.dataset.groupName
    }
    if (this.hasRemoveLinkTarget) {
      this.removeLinkTarget.href = sortableTab.dataset.removeUrl
    }
  }

  sortableTabTargetConnected(el) {
    el.draggable = true
  }

  addMoveLink(event) {
    const { template } = event.detail
    this.formTargets.forEach(form => {
      const header = this.moveHeaderTargets.find(el => form.contains(el))
      if (header) header.classList.remove('d-none')

      const container = this.moveLinkTargets.find(el => form.contains(el))?.parentElement
      if (!container) return
      container.insertAdjacentHTML('beforeend', template.replace('__IMO__', form.dataset.imo))
    })
  }

  dragStart(event) {
    this.draggedTab = event.currentTarget
    event.dataTransfer.effectAllowed = 'move'
    requestAnimationFrame(() => this.draggedTab?.classList.add('is-dragging'))
  }

  dragOver(event) {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.draggedTab || target === this.draggedTab) return
    const rect = target.getBoundingClientRect()
    const insertAfter = event.clientX > rect.left + rect.width / 2
    this.tabsContainerTarget.insertBefore(this.draggedTab, insertAfter ? target.nextSibling : target)
  }

  dragEnd() {
    this.draggedTab?.classList.remove('is-dragging')
    const ids = this.sortableTabTargets.map(tab => tab.dataset.groupId)
    $.ajax({ url: `/vessel-groups/reorder?type=${this.typeValue}`, method: 'PATCH', data: { ids } })
    this.draggedTab = null
  }

  move(event) {
    event.preventDefault()

    const imo = event.currentTarget.dataset.imo
    const groupId = event.currentTarget.dataset.groupId
    const form = this.formTargets.find(el => el.dataset.imo === imo)
    if (!form) return
    const panel = this.panelTargets.find(el => el.dataset.panel === (groupId || 'default'))
    if (!panel) return

    const addVesselWrapper = this.addVesselWrapperTargets.find(w => w.dataset.panel === (groupId || 'default'))
    if (addVesselWrapper) {
      panel.insertBefore(form, addVesselWrapper)
    } else {
      panel.appendChild(form)
    }

    const input = this.groupInputTargets.find(el => form.contains(el))
    const previousGroupId = input ? input.value : ''
    if (input) input.value = groupId
    this.updateMoveLinks(form, previousGroupId, groupId)
    this.persistGroupAssignment(imo, groupId, previousGroupId)
    this.updateCounts()
    window.dispatchEvent(new CustomEvent('vessel-group:changed'))
  }

  persistGroupAssignment(imo, groupId, previousGroupId) {
    const type = this.typeValue
    if (groupId) {
      $.ajax({ url: `/vessel-groups/${groupId}/vessels/${imo}?type=${type}`, method: 'PATCH' })
    } else if (previousGroupId) {
      $.ajax({ url: `/vessel-groups/${previousGroupId}/vessels/${imo}?type=${type}`, method: 'DELETE' })
    }
  }

  updateMoveLinks(form, previousGroupId, newGroupId) {
    const moveLinks = this.moveLinkTargets.filter(el => form.contains(el))
    const previousLi = moveLinks.find(el => el.dataset.groupId === previousGroupId)
    if (previousLi) previousLi.classList.remove('d-none')
    const newLi = moveLinks.find(el => el.dataset.groupId === newGroupId)
    if (newLi) newLi.classList.add('d-none')
  }

  updateCounts() {
    this.panelTargets.forEach(panel => {
      const count = this.formTargets.filter(form => panel.contains(form)).length
      const countEl = this.countTargets.find(el => el.dataset.panel === panel.dataset.panel)
      if (countEl) countEl.textContent = `(${count})`
    })
  }
}
